import * as React from 'react'
import { toast } from 'sonner'

import type { DutyBranch } from '@/features/duties/branches'
import { DutyToolError } from '@/features/duties/types'
import type {
  DutyAuthenticatePersonSettings,
  DutyHandoffToDutySettings,
  DutyToolKey,
  DutyTransferCallSettings,
} from '@/features/duties/types'
import {
  useDutyToolsInfo,
  useDutyPatientMemory,
  useDutyWorkflows,
  useUpdateDutyPatientMemory,
  useUpdateDutyFirstMessage,
  useUpdateDutyTool,
} from '@/hooks/useDutyDetail'
import {
  useDutyMcpConnections,
  useDutyTools,
  useExcludeDutyTool,
  useIncludeDutyTool,
  useSetDutyMcpConnection,
} from '@/hooks/useToolsAdmin'
import { useOrgWorkflowsQuery } from '@/features/workflows/hooks/useWorkflowsQuery'
import { useCardVisibilityStore } from '@/stores/cardVisibilityStore'
import { ClearFirstMessageDialog } from '@/features/duties/components/cards/FastFirstMessageCard/ClearFirstMessageDialog'
import {
  CONFIGURATION_PILL_KEYS,
  CREATE_NEW_PATIENT_LABEL,
  DUTY_TOOL_NAV,
  NEW_PATIENT_WORKFLOW_SLUG,
  UNPERSISTED_PILL_KEYS,
  WORKFLOW_TOOL_SLOTS,
} from '@/features/duties/components/detail/dutyCardNav'
import type { DutyCardId } from '@/features/duties/components/detail/dutyCardNav'
import { useFetchErrorToast } from '@/features/duties/hooks/useFetchErrorToast'
import { toolDisplayName } from '@/features/tools/presentation'
import type { ToolKind } from '@/features/tools/types'
import { useMountEffect } from '@/hooks/useMountEffect'

// Tool pills, derived from the canonical card-nav list so order + labels stay
// in lockstep with the toolbar. Operators hide Voicemail and workflow-backed
// entries are capability-gated in `visibleToolsPills`.
export const BASE_TOOLS_PILLS: ReadonlyArray<{
  key: DutyToolKey
  label: string
}> = DUTY_TOOL_NAV.map((entry) => ({
  key: entry.toolKey,
  label: entry.label,
}))

export const TOOLS_PILLS: ReadonlyArray<{
  key: DutyToolKey
  label: string
}> = [...BASE_TOOLS_PILLS, { key: 'complain', label: 'Complaint Box' }]

/** The pill keys whose tool is provided by a Nova workflow, not by Hoku. */
const WORKFLOW_TOOL_KEYS: ReadonlySet<DutyToolKey> = new Set(
  WORKFLOW_TOOL_SLOTS.map((slot) => slot.toolKey)
)

/**
 * An in-call tool a Nova workflow provides, resolved for one duty: the pill to
 * offer, and everything its card needs to render itself.
 */
export type InCallWorkflowTool = {
  /** The pill's key — names the Mercury capability the workflow stands in for. */
  toolKey: DutyToolKey
  /** The nav slot the card registers under; fixes its icon and toolbar order. */
  cardId: DutyCardId
  /** Nebula's identifier for the workflow — what the card reads variants by. */
  slug: string
  /** Whether this duty currently holds the workflow registration. */
  registered: boolean
  /**
   * Product label for the pill and card. Usually follows the workflow title;
   * product-defined cards may keep a generic label across organizations.
   */
  label: string
}

const NO_IN_CALL_WORKFLOW_TOOLS: ReadonlyArray<InCallWorkflowTool> = []

/**
 * An organization tool (`app_tools`), resolved for one duty through
 * its `app_duty_tools` inclusion state. Distinct from `InCallWorkflowTool`
 * above — these tools are managed through the Tools admin page rather than
 * Nova workflows, and their on/off state is row-existence in
 * `app_duty_tools` (see `list_duty_tools` on relay) rather than an
 * `app_duties.tools` enum entry.
 */
export type OrgToolPill = {
  toolId: string
  label: string
  kind: ToolKind
  included: boolean
  icon: string | null
}

export type DutyMcpConnectionPill = {
  connectionId: string
  label: string
  included: boolean
  availability: 'available' | 'reconnect_required' | 'unavailable'
}

const NO_ORG_TOOL_PILLS: ReadonlyArray<OrgToolPill> = []

/**
 * Which tool pills a duty exposes:
 *   - Operator duties never expose "Voicemail" (the operator flow doesn't play
 *     voicemail). Operator status is `tools.is_operator` from the server.
 *   - Workflow-provided tools are capability-gated: a pill appears only for a
 *     workflow in `workflowTools`, and takes its label from the workflow rather
 *     than from the nav entry, so renaming a workflow renames its pill.
 * Every other pill is available to all duties.
 */
export function visibleToolsPills(
  isOperator: boolean,
  workflowTools: ReadonlyArray<InCallWorkflowTool>
): ReadonlyArray<{ key: DutyToolKey; label: string }> {
  const byToolKey = new Map(workflowTools.map((t) => [t.toolKey, t]))
  const pills: Array<{ key: DutyToolKey; label: string }> = []
  for (const p of TOOLS_PILLS) {
    if (p.key === 'voicemail' && isOperator) continue
    if (WORKFLOW_TOOL_KEYS.has(p.key)) {
      const tool = byToolKey.get(p.key)
      if (!tool) continue
      pills.push({ key: p.key, label: tool.label })
      continue
    }
    pills.push(p)
  }
  return pills
}

const ALL_POSSIBLE_PILL_KEYS: ReadonlySet<DutyToolKey> = new Set<DutyToolKey>(
  TOOLS_PILLS.map((p) => p.key)
)

const TOOL_LABELS: ReadonlyMap<DutyToolKey, string> = new Map(
  TOOLS_PILLS.map((p) => [p.key, p.label])
)

type DutyDetailToolsContextValue = {
  selectedTools: Set<DutyToolKey>
  toggleTool: (key: DutyToolKey, enabled?: boolean) => void
  isToolsLoading: boolean
  isToolsError: boolean
  toolsError: unknown
  /** True while a tool toggle is persisting; switches disable to keep
   * concurrent toggles from clobbering each other's tools writes. */
  isToolMutationPending: boolean
  transferCallSettings: DutyTransferCallSettings | undefined
  handoffToDutySettings: DutyHandoffToDutySettings | undefined
  authenticatePersonSettings: DutyAuthenticatePersonSettings | undefined
  pills: ReadonlyArray<{ key: DutyToolKey; label: string }>
  /**
   * The workflow-provided in-call tools this duty offers, in nav order. Drives
   * the cards on the call screen (see DutyCallPage) — one per tool — so the card
   * list and the pill list come from the same derivation.
   */
  inCallWorkflowTools: ReadonlyArray<InCallWorkflowTool>
  isWorkflowCatalogueError: boolean
  retryWorkflowCatalogue: () => void
  isOperator: boolean
  /**
   * Org-authored tools available to the selected duty branch. Live resolves
   * relational associations; Edit and Review resolve their stored snapshots.
   */
  orgToolPills: ReadonlyArray<OrgToolPill>
  toggleOrgTool: (toolId: string, enabled: boolean, label: string) => void
  isOrgToolsLoading: boolean
  isOrgToolsError: boolean
  isOrgToolMutationPending: boolean
  mcpConnectionsReadOnly?: boolean
  mcpConnectionPills: ReadonlyArray<DutyMcpConnectionPill>
  toggleMcpConnection: (
    connectionId: string,
    enabled: boolean,
    label: string
  ) => void
  isMcpConnectionsLoading: boolean
  isMcpConnectionsError: boolean
  isMcpConnectionMutationPending: boolean
}

const DutyNormalizedToolsReadOnlyContext = React.createContext<
  boolean | undefined
>(undefined)

export function useDutyNormalizedToolsReadOnly(): boolean {
  const readOnly = React.useContext(DutyNormalizedToolsReadOnlyContext)
  if (readOnly === undefined) {
    throw new Error(
      'useDutyNormalizedToolsReadOnly must be used within DutyDetailToolsProvider'
    )
  }
  return readOnly
}

const DutyDetailToolsContext = React.createContext<
  DutyDetailToolsContextValue | undefined
>(undefined)

export function DutyDetailToolsProvider({
  dutyId,
  normalizedToolBranch,
  normalizedToolsReadOnly,
  mcpConnectionsReadOnly = false,
  children,
}: {
  dutyId: string
  normalizedToolBranch: DutyBranch | null
  normalizedToolsReadOnly: boolean
  mcpConnectionsReadOnly?: boolean
  children: React.ReactNode
}) {
  const {
    data: tools,
    isLoading: isDutyToolsLoading,
    isError: isDutyToolsError,
    error: dutyToolsError,
  } = useDutyToolsInfo(dutyId)

  const { data: patientMemory } = useDutyPatientMemory(dutyId)
  const updatePatientMemory = useUpdateDutyPatientMemory(dutyId)
  const { mutate: mutatePatientMemory } = updatePatientMemory

  const updateTool = useUpdateDutyTool(dutyId)
  const { mutate: mutateTool } = updateTool

  // Available Nova workflows gate the workflow-provided tool pills: a workflow
  // declaring `provides_in_call_tool` is what makes such a tool worth offering
  // at all. Deprecated entries don't count — Nebula still lists a retired
  // workflow (its YAML stays on disk so historical sessions resolve) but refuses
  // to invoke it, so offering the toggle would only enable a tool whose every
  // session gets rejected. Scoped to the org, not the duty, so walking through
  // duties doesn't refetch a catalogue identical for all.
  const {
    data: orgWorkflows,
    isError: isWorkflowCatalogueError,
    refetch: refetchWorkflowCatalogue,
  } = useOrgWorkflowsQuery()

  // ...while the duty-scoped listing is the source of truth for whether a
  // workflow-backed tool is enabled for this duty, and the only thing the
  // toggle writes (see patch_duty_tools_handler). The registration decides
  // whether the workflow card is visible even when the server also manages a
  // Mercury capability tag for that workflow.
  const { data: dutyWorkflows } = useDutyWorkflows(dutyId)

  // Organization tools for this duty branch. `list_duty_tools` returns every
  // available org tool with `included: false` when its selected branch omits
  // the association, so disabled-by-default state needs no backfill.
  const {
    data: orgDutyTools,
    isLoading: isOrgToolsLoading,
    isError: isOrgToolsError,
  } = useDutyTools(dutyId, true, normalizedToolBranch)
  const includeOrgTool = useIncludeDutyTool(dutyId, normalizedToolBranch)
  const excludeOrgTool = useExcludeDutyTool(dutyId, normalizedToolBranch)
  const {
    data: dutyMcpConnections,
    isLoading: isMcpConnectionsLoading,
    isError: isMcpConnectionsError,
  } = useDutyMcpConnections(dutyId, true, normalizedToolBranch)
  const setDutyMcpConnection = useSetDutyMcpConnection(
    dutyId,
    normalizedToolBranch
  )

  const mcpConnectionPills = React.useMemo<
    ReadonlyArray<DutyMcpConnectionPill>
  >(
    () =>
      (dutyMcpConnections ?? []).map((connection) => ({
        connectionId: connection.connection_id,
        label: connection.name,
        included: connection.included,
        availability: connection.availability,
      })),
    [dutyMcpConnections]
  )

  const toggleMcpConnection = React.useCallback(
    (connectionId: string, enabled: boolean, label: string) => {
      setDutyMcpConnection.mutate(
        { connectionId, enabled },
        {
          onSuccess: () =>
            toast.success(`${label} ${enabled ? 'enabled' : 'disabled'}`),
          onError: (error) =>
            toast.error(
              error instanceof Error
                ? error.message
                : `Failed to ${enabled ? 'enable' : 'disable'} ${label}`
            ),
        }
      )
    },
    [setDutyMcpConnection]
  )

  const orgToolPills = React.useMemo<ReadonlyArray<OrgToolPill>>(() => {
    if (!orgDutyTools || orgDutyTools.length === 0) return NO_ORG_TOOL_PILLS
    // relay's list_duty_tools already scopes to angelica_enabled tools;
    // filter again here so the popover never shows a tool an admin has since
    // disabled for Angelica, even if a stale cache briefly holds one.
    const enabledTools = orgDutyTools.filter(
      (item) => item.tool.angelica_enabled
    )
    if (enabledTools.length === 0) return NO_ORG_TOOL_PILLS
    return enabledTools.map((item) => ({
      toolId: item.tool_id,
      label: toolDisplayName(item.tool),
      kind: item.tool.kind,
      included: item.included,
      icon: item.tool.icon,
    }))
  }, [orgDutyTools])

  const toggleOrgTool = React.useCallback(
    (toolId: string, enabled: boolean, label: string) => {
      if (enabled) {
        includeOrgTool.mutate(
          { toolId },
          {
            onSuccess: () => {
              toast.success(`${label} enabled successfully`)
            },
            onError: (error) => {
              toast.error(
                error instanceof Error
                  ? error.message
                  : `Failed to enable ${label}`
              )
            },
          }
        )
        return
      }
      excludeOrgTool.mutate(toolId, {
        onSuccess: () => {
          toast.success(`${label} disabled successfully`)
        },
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : `Failed to disable ${label}`
          )
        },
      })
    },
    [includeOrgTool, excludeOrgTool]
  )

  const isOrgToolMutationPending =
    includeOrgTool.isPending || excludeOrgTool.isPending

  const toolKeys = tools?.tools
  const inCallWorkflowTools = React.useMemo<
    ReadonlyArray<InCallWorkflowTool>
  >(() => {
    const resolved: InCallWorkflowTool[] = []
    for (const slot of WORKFLOW_TOOL_SLOTS) {
      const catalogued = orgWorkflows?.find((w) => w.slug === slot.slug)
      const offered =
        catalogued !== undefined &&
        catalogued.provides_in_call_tool &&
        !catalogued.deprecated
      const registered =
        dutyWorkflows?.workflows.some((w) => w.slug === slot.slug) === true
      // A duty that still owns the registration keeps its pill even once the
      // workflow retires or drops the flag, so it retains a control to turn the
      // registration off. The Mercury tag in app_duties.tools is deliberately
      // ignored here — it is provisioned out of band and says only that the
      // in-call tool is advertised: Nebula alone owns whether the workflow is
      // registered to this duty.
      if (!offered && !registered) continue
      resolved.push({
        toolKey: slot.toolKey,
        cardId: slot.cardId,
        slug: slot.slug,
        registered,
        label:
          slot.slug === NEW_PATIENT_WORKFLOW_SLUG
            ? (TOOL_LABELS.get(slot.toolKey) ?? CREATE_NEW_PATIENT_LABEL)
            : (catalogued?.title ??
              dutyWorkflows?.workflows.find((w) => w.slug === slot.slug)
                ?.title ??
              TOOL_LABELS.get(slot.toolKey) ??
              slot.slug),
      })
    }
    return resolved.length > 0 ? resolved : NO_IN_CALL_WORKFLOW_TOOLS
  }, [orgWorkflows, dutyWorkflows])

  // Disabling Fast First Message with a message saved must clear
  // config.firstMessage (the relay applies it whenever it's non-empty and the
  // server derives the toggle from it) — confirmed via dialog before deleting.
  // The dialog owns the clear flow; the mutation instance lives here so its
  // isPending feeds isToolMutationPending (disabling the popover switches).
  const clearFirstMessage = useUpdateDutyFirstMessage(dutyId)
  const [isClearFirstMessageDialogOpen, setClearFirstMessageDialogOpen] =
    React.useState(false)

  // Unpersisted pills only show/hide their cards; visibility overrides live
  // in a local store on top of the server-derived default.
  const visibilityOverrides = useCardVisibilityStore((s) =>
    s.getOverrides(dutyId)
  )
  const setCardVisible = useCardVisibilityStore((s) => s.setCardVisible)

  const isOperator = tools?.is_operator === true

  // Tool selection is server state. Ordinary persisted tools read the tools
  // query; workflow-backed tools read only the duty's Nebula registrations.
  // One PATCH serves both — it writes whichever store owns the key — and the
  // optimistic update patches the matching cache (see useUpdateDutyTool), so
  // selection survives page refreshes. Unpersisted
  // pills instead resolve local override ?? default, where the default is:
  //   - Post-Call pills (labels/facts_extraction/resequence): on
  //   - Fast First Message: server-derived (on when a first message is set)
  // Operator duties exclude voicemail even when persisted — their popover has
  // no voicemail pill, so a surfaced card would have no toggle to dismiss it.
  // Workflow-provided tools are the exception: the Nebula registration alone
  // determines whether the workflow card and pill are on for this duty, and it
  // is the only thing their toggle writes.
  // Server-derived: fast_first_message is in the tools list iff a first
  // message is currently saved on the duty (see get_duty_tools_handler).
  const hasSavedFirstMessage = toolKeys?.includes('fast_first_message') ?? false
  const selectedTools = React.useMemo(() => {
    const serverKeys = new Set<DutyToolKey>(toolKeys ?? [])
    const selected = new Set<DutyToolKey>()
    for (const k of ALL_POSSIBLE_PILL_KEYS) {
      if (isOperator && k === 'voicemail') continue
      const defaultVisible =
        k === 'patient_memory'
          ? patientMemory?.enabled === true
          : CONFIGURATION_PILL_KEYS.has(k)
            ? true
            : serverKeys.has(k)
      const workflowTool = inCallWorkflowTools.find(
        (tool) => tool.toolKey === k
      )
      const visible =
        k === 'patient_memory'
          ? defaultVisible
          : WORKFLOW_TOOL_KEYS.has(k)
            ? workflowTool?.registered === true
            : UNPERSISTED_PILL_KEYS.has(k)
              ? (visibilityOverrides?.[k] ?? defaultVisible)
              : serverKeys.has(k)
      if (visible) selected.add(k)
    }
    return selected
  }, [
    toolKeys,
    visibilityOverrides,
    isOperator,
    inCallWorkflowTools,
    patientMemory?.enabled,
  ])

  const toggleTool = React.useCallback(
    (key: DutyToolKey, requestedEnabled?: boolean) => {
      const enabled = requestedEnabled ?? !selectedTools.has(key)
      const label = TOOL_LABELS.get(key) ?? key
      if (key === 'fast_first_message' && !enabled && hasSavedFirstMessage) {
        // Hiding the card alone wouldn't stop the override — the saved
        // message must be cleared, so confirm before deleting it.
        setClearFirstMessageDialogOpen(true)
        return
      }
      if (key === 'patient_memory') {
        mutatePatientMemory(
          { enabled },
          {
            onSuccess: () =>
              toast.success(`Memory ${enabled ? 'enabled' : 'disabled'}`),
            onError: () => toast.error('Failed to update memory'),
          }
        )
        return
      }
      if (UNPERSISTED_PILL_KEYS.has(key)) {
        // These pills only show/hide their card — no backend write — so the
        // toast reflects visibility, not enable/disable persistence.
        setCardVisible(dutyId, key, enabled)
        toast.success(`${label} ${enabled ? 'shown' : 'hidden'}`)
        return
      }
      mutateTool(
        { tool: key, enabled },
        {
          onSuccess: () => {
            toast.success(
              `${label} ${enabled ? 'enabled' : 'disabled'} successfully`
            )
          },
          onError: (error) => {
            // A refusal the server explained is shown as it wrote it — enabling
            // a workflow-backed tool another duty already holds names that duty,
            // which is the whole point of the message. Anything else keeps the
            // generic line, so a transport failure doesn't read as one.
            toast.error(
              error instanceof DutyToolError
                ? error.message
                : `Failed to ${enabled ? 'enable' : 'disable'} ${label}`
            )
          },
        }
      )
    },
    [
      selectedTools,
      mutateTool,
      dutyId,
      setCardVisible,
      hasSavedFirstMessage,
      mutatePatientMemory,
    ]
  )

  const pills = React.useMemo(
    () => visibleToolsPills(isOperator, inCallWorkflowTools),
    [isOperator, inCallWorkflowTools]
  )

  const isToolMutationPending =
    updateTool.isPending ||
    clearFirstMessage.isPending ||
    updatePatientMemory.isPending
  // Patient memory owns its Relay loading/error state. An unavailable memory
  // endpoint must not hide or disable unrelated tool cards.
  const isToolsLoading = isDutyToolsLoading
  const isToolsError = isDutyToolsError
  const toolsError = dutyToolsError
  const retryWorkflowCatalogue = React.useCallback(() => {
    void refetchWorkflowCatalogue()
  }, [refetchWorkflowCatalogue])
  const value = React.useMemo<DutyDetailToolsContextValue>(
    () => ({
      selectedTools,
      toggleTool,
      isToolsLoading,
      isToolsError,
      toolsError,
      isToolMutationPending,
      transferCallSettings: tools?.transfer_call_settings,
      handoffToDutySettings: tools?.handoff_to_duty_settings,
      authenticatePersonSettings: tools?.authenticate_person_settings,
      pills,
      inCallWorkflowTools,
      isWorkflowCatalogueError,
      retryWorkflowCatalogue,
      isOperator,
      orgToolPills,
      toggleOrgTool,
      isOrgToolsLoading,
      isOrgToolsError,
      isOrgToolMutationPending,
      mcpConnectionsReadOnly,
      mcpConnectionPills,
      toggleMcpConnection,
      isMcpConnectionsLoading,
      isMcpConnectionsError,
      isMcpConnectionMutationPending: setDutyMcpConnection.isPending,
    }),
    [
      selectedTools,
      toggleTool,
      isToolsLoading,
      isToolsError,
      toolsError,
      isToolMutationPending,
      tools?.transfer_call_settings,
      tools?.handoff_to_duty_settings,
      tools?.authenticate_person_settings,
      pills,
      inCallWorkflowTools,
      isWorkflowCatalogueError,
      retryWorkflowCatalogue,
      isOperator,
      orgToolPills,
      toggleOrgTool,
      isOrgToolsLoading,
      isOrgToolsError,
      isOrgToolMutationPending,
      mcpConnectionsReadOnly,
      mcpConnectionPills,
      toggleMcpConnection,
      isMcpConnectionsLoading,
      isMcpConnectionsError,
      setDutyMcpConnection.isPending,
    ]
  )

  return (
    <DutyNormalizedToolsReadOnlyContext.Provider
      value={normalizedToolsReadOnly}>
      <DutyDetailToolsContext.Provider value={value}>
        {isToolsError && <ToolsFetchErrorReporter />}
        {children}
        {/* On success the mutation's awaited invalidation refetches the tools
            query, so the server-derived selection drops fast_first_message and
            the card hides on its own — no local visibility override, which
            would go stale if a message reappeared in the same session. */}
        <ClearFirstMessageDialog
          mutation={clearFirstMessage}
          open={isClearFirstMessageDialogOpen}
          onOpenChange={setClearFirstMessageDialogOpen}
        />
      </DutyDetailToolsContext.Provider>
    </DutyNormalizedToolsReadOnlyContext.Provider>
  )
}

/**
 * Reports a tools fetch failure once, on mount, and clears it on unmount. A
 * component rather than an effect in the provider so the toast is tied to the
 * error being current, not to every provider render.
 */
function ToolsFetchErrorReporter() {
  const { reportFetchError, reset } = useFetchErrorToast()
  useMountEffect(() => {
    reportFetchError('Tools')
    return reset
  })
  return null
}

export function useDutyDetailTools() {
  const ctx = React.useContext(DutyDetailToolsContext)
  if (!ctx) {
    throw new Error(
      'useDutyDetailTools must be used within DutyDetailToolsProvider'
    )
  }
  return ctx
}

type ScrollContainerRef = React.RefObject<HTMLDivElement | null>

const DutyDetailScrollContainerContext = React.createContext<
  ScrollContainerRef | undefined
>(undefined)

export function DutyDetailScrollContainerProvider({
  scrollContainerRef,
  children,
}: {
  scrollContainerRef: ScrollContainerRef
  children: React.ReactNode
}) {
  return (
    <DutyDetailScrollContainerContext.Provider value={scrollContainerRef}>
      {children}
    </DutyDetailScrollContainerContext.Provider>
  )
}

export function useDutyDetailScrollContainerRef() {
  const ctx = React.useContext(DutyDetailScrollContainerContext)
  if (!ctx) {
    throw new Error(
      'useDutyDetailScrollContainerRef must be used within DutyDetailScrollContainerProvider'
    )
  }
  return ctx
}

/** When true, duty detail cards should not show edit actions (e.g. Cancel / Save). */
const DutyDetailArchivedContext = React.createContext(false)

export function DutyDetailArchivedProvider({
  isArchived,
  children,
}: {
  isArchived: boolean
  children: React.ReactNode
}) {
  return (
    <DutyDetailArchivedContext.Provider value={isArchived}>
      {children}
    </DutyDetailArchivedContext.Provider>
  )
}

export function useDutyDetailArchived() {
  return React.useContext(DutyDetailArchivedContext)
}

/**
 * Page-scoped lock for Live-only duty content while the script switcher is
 * showing a non-Live branch. This is deliberately separate from archived:
 * archived remains the stronger duty state, while this only suppresses Live
 * writes for cards placed under the provider.
 */
const DutyDetailLiveContentReadOnlyContext = React.createContext(false)

export function DutyDetailLiveContentReadOnlyProvider({
  readOnly,
  children,
}: {
  readOnly: boolean
  children: React.ReactNode
}) {
  return (
    <DutyDetailLiveContentReadOnlyContext.Provider value={readOnly}>
      {children}
    </DutyDetailLiveContentReadOnlyContext.Provider>
  )
}

export function useDutyDetailLiveContentReadOnly() {
  return React.useContext(DutyDetailLiveContentReadOnlyContext)
}

// --- Scroll-to-card registry -------------------------------------------------
// Cards register their anchor node (via `DutyCardAnchor`) so the toolbar can
// scroll-nav to them by id. Ref-callback based: no effects, no `document`
// queries. Registered ids are tracked reactively because static cards aren't
// `selectedTools`-gated, so their presence (e.g. admin- or super-user-only
// cards) is the only signal the toolbar has that they're on the page.

type DutyCardRegistryValue = {
  register: (key: string, node: HTMLElement | null) => void
  scrollTo: (key: string) => void
  registeredIds: ReadonlySet<string>
}

const DutyCardRegistryContext = React.createContext<
  DutyCardRegistryValue | undefined
>(undefined)

export function DutyCardRegistryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const nodesRef = React.useRef(new Map<string, HTMLElement>())
  const [registeredIds, setRegisteredIds] = React.useState<ReadonlySet<string>>(
    () => new Set<string>()
  )

  const register = React.useCallback(
    (key: string, node: HTMLElement | null) => {
      if (node) {
        nodesRef.current.set(key, node)
        setRegisteredIds((prev) => {
          if (prev.has(key)) return prev
          const next = new Set(prev)
          next.add(key)
          return next
        })
      } else {
        nodesRef.current.delete(key)
        setRegisteredIds((prev) => {
          if (!prev.has(key)) return prev
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    },
    []
  )

  const scrollTo = React.useCallback((key: string) => {
    const node = nodesRef.current.get(key)
    if (!node) return
    // Respect the user's OS-level reduced-motion preference (WCAG 2.3.3).
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    node.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    })
  }, [])

  const value = React.useMemo(
    () => ({ register, scrollTo, registeredIds }),
    [register, scrollTo, registeredIds]
  )

  return (
    <DutyCardRegistryContext.Provider value={value}>
      {children}
    </DutyCardRegistryContext.Provider>
  )
}

function useDutyCardRegistry() {
  const ctx = React.useContext(DutyCardRegistryContext)
  if (!ctx) {
    throw new Error(
      'useDutyCardRegistry must be used within DutyCardRegistryProvider'
    )
  }
  return ctx
}

/** Stable ref callback that registers a card's anchor node for scroll-nav. */
export function useRegisterDutyCard(key: string) {
  const { register } = useDutyCardRegistry()
  return React.useCallback(
    (node: HTMLElement | null) => register(key, node),
    [register, key]
  )
}

/** Returns `scrollToCard(key)` — smooth-scrolls a registered card into view. */
export function useScrollToDutyCard() {
  return useDutyCardRegistry().scrollTo
}

/** The set of card ids currently mounted (registered) on the page. */
export function useRegisteredDutyCards() {
  return useDutyCardRegistry().registeredIds
}
